import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Card, Button, Tag, message, Table, Spin } from "antd";
import {
  CreditCardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { setUserInfo } from "../../../store/features/UserInfo";
import { Container } from "./AccountSettingsStyle";

const GET_COACH_SUBSCRIPTION_STATUS = gql`
  query getCoachSubscriptionStatus {
    getCoachSubscriptionStatus {
      status
      hasActiveSubscription
      trialEndDate
      trialStartDate
      currentPeriodStart
      currentPeriodEnd
      cancelAtPeriodEnd
      planType
    }
  }
`;

const GET_COACH_INVOICES = gql`
  query getCoachInvoices {
    getCoachInvoices {
      amount
      currency
      invoiceUrl
      invoicePdf
      periodStart
      periodEnd
      status
      stripeInvoiceId
    }
  }
`;

const CREATE_BILLING_PORTAL_SESSION = gql`
  mutation createBillingPortalSession {
    createBillingPortalSession {
      url
    }
  }
`;

const STATUS_CONFIG = {
  TRIAL: {
    color: "processing",
    icon: <ClockCircleOutlined />,
    text: "Start Free Trial",
  },
  TRIALING: {
    color: "processing",
    icon: <ClockCircleOutlined />,
    text: "Trial",
  },
  ACTIVE: {
    color: "success",
    icon: <CheckCircleOutlined />,
    text: "Active",
  },
  CANCELED: {
    color: "error",
    icon: <CloseCircleOutlined />,
    text: "Canceled",
  },
  CANCELLED: {
    color: "error",
    icon: <CloseCircleOutlined />,
    text: "Canceled",
  },
  PAST_DUE: {
    color: "warning",
    icon: <ExclamationCircleOutlined />,
    text: "Past Due",
  },
  UNPAID: {
    color: "error",
    icon: <ExclamationCircleOutlined />,
    text: "Unpaid",
  },
  INCOMPLETE: {
    color: "warning",
    icon: <ExclamationCircleOutlined />,
    text: "Incomplete",
  },
  INCOMPLETE_EXPIRED: {
    color: "error",
    icon: <CloseCircleOutlined />,
    text: "Expired",
  },
  PAUSED: {
    color: "default",
    icon: <PauseCircleOutlined />,
    text: "Paused",
  },
};

export default function AccountSettings() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo.userInfo);

  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [getCoachSubscriptionStatus] = useLazyQuery(
    GET_COACH_SUBSCRIPTION_STATUS,
    {
      fetchPolicy: "network-only", // Always fetch fresh data
    }
  );

  const [getCoachInvoices] = useLazyQuery(GET_COACH_INVOICES, {
    fetchPolicy: "network-only", // Always fetch fresh data
  });

  const [createBillingPortalSession] = useMutation(
    CREATE_BILLING_PORTAL_SESSION
  );

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const result = await createBillingPortalSession({
        variables: {},
      });
      const url = result?.data?.createBillingPortalSession?.url;
      if (url) {
        window.open(url, "_blank");
      } else {
        throw new Error("Failed to get billing portal URL");
      }
    } catch (error) {
      message.error(
        error?.message || "Failed to create billing portal session"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      setDataLoading(true);
      try {
        const result = await getCoachSubscriptionStatus();
        const subscriptionData = result?.data?.getCoachSubscriptionStatus;

        if (subscriptionData) {
          setSubscription(subscriptionData);

          // Update store with latest subscription status
          dispatch(
            setUserInfo({
              ...userInfo,
              subscriptionStatus: subscriptionData,
            })
          );
        }
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
        message.error("Failed to load subscription information");
      } finally {
        setDataLoading(false);
      }
    };
    fetchSubscriptionStatus();

    const fetchInvoices = async () => {
      const result = await getCoachInvoices();
      const invoicesData = result?.data?.getCoachInvoices;
      setInvoices(invoicesData);
    };
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgrade = () => {
    navigate("/pricing");
  };

  const statusConfig = useMemo(() => {
    if (!subscription?.status) return STATUS_CONFIG.TRIAL;
    return STATUS_CONFIG[subscription.status] || STATUS_CONFIG.TRIAL;
  }, [subscription?.status]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  const hasActiveTrial = useMemo(() => {
    if (!subscription?.trialEndDate) return false;
    return new Date(subscription.trialEndDate) > new Date();
  }, [subscription?.trialEndDate]);

  // Table data for subscription plans
  const tableData = useMemo(() => {
    const data = [];

    // Trial row - show if there's trial data (even if expired)
    if (
      subscription?.status === "TRIALING" ||
      subscription?.trialStartDate ||
      subscription?.trialEndDate
    ) {
      const isExpired =
        subscription?.trialEndDate &&
        new Date(subscription.trialEndDate) < new Date();

      data.push({
        key: "trial",
        plan: (
          <span style={{ fontSize: "16px", fontWeight: 600 }}>
            Free Trial
            {isExpired && (
              <Tag color="default" style={{ marginLeft: "8px" }}>
                Expired
              </Tag>
            )}
            {!isExpired && hasActiveTrial && (
              <Tag color="blue" style={{ marginLeft: "8px" }}>
                Current
              </Tag>
            )}
          </span>
        ),
        startTime: formatDate(subscription?.trialStartDate),
        endTime: formatDate(subscription?.trialEndDate),
        price: (
          <span style={{ color: "#52c41a", fontWeight: 600, fontSize: "16px" }}>
            $0
          </span>
        ),
      });
    }

    // Only show the plan user currently has or had
    const currentPlanType = subscription?.planType?.toUpperCase();

    const isCanceled =
      subscription?.status === "CANCELED" ||
      subscription?.status === "CANCELLED";

    // Yearly row - only show if user has/had yearly plan
    if (currentPlanType === "YEARLY") {
      data.push({
        key: "yearly",
        plan: (
          <span style={{ fontSize: "16px", fontWeight: 600 }}>
            Yearly Plan
            <Tag
              color={isCanceled ? "error" : "blue"}
              style={{ marginLeft: "8px" }}
            >
              {isCanceled ? "Canceled" : "Current"}
            </Tag>
          </span>
        ),
        startTime: formatDate(subscription?.currentPeriodStart),
        endTime: formatDate(subscription?.currentPeriodEnd),
        price: (
          <span style={{ fontWeight: 600, fontSize: "16px" }}>
            $180
            <span style={{ fontSize: "14px", color: "#8c8c8c" }}>/year</span>
          </span>
        ),
      });
    }

    // Monthly row - only show if user has/had monthly plan
    if (currentPlanType === "MONTHLY") {
      data.push({
        key: "monthly",
        plan: (
          <span style={{ fontSize: "16px", fontWeight: 600 }}>
            Monthly Plan
            <Tag
              color={isCanceled ? "error" : "blue"}
              style={{ marginLeft: "8px" }}
            >
              {isCanceled ? "Canceled" : "Current"}
            </Tag>
          </span>
        ),
        startTime: formatDate(subscription?.currentPeriodStart),
        endTime: formatDate(subscription?.currentPeriodEnd),
        price: (
          <span style={{ fontWeight: 600, fontSize: "16px" }}>
            $20
            <span style={{ fontSize: "14px", color: "#8c8c8c" }}>/month</span>
          </span>
        ),
      });
    }

    return data;
  }, [subscription, hasActiveTrial, formatDate]);

  const columns = [
    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
    },
    {
      title: "Start Time",
      dataIndex: "startTime",
      key: "startTime",
    },
    {
      title: "End Time",
      dataIndex: "endTime",
      key: "endTime",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
    },
  ];

  // Invoice status config
  const INVOICE_STATUS_CONFIG = {
    PAID: { color: "success", text: "Paid" },
    OPEN: { color: "processing", text: "Open" },
    VOID: { color: "default", text: "Void" },
    UNCOLLECTIBLE: { color: "error", text: "Uncollectible" },
    DRAFT: { color: "default", text: "Draft" },
  };

  // Invoice columns
  const invoiceColumns = [
    {
      title: "Date",
      dataIndex: "periodStart",
      key: "periodStart",
      render: (periodStart) => formatDate(periodStart),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const config =
          INVOICE_STATUS_CONFIG[status] || INVOICE_STATUS_CONFIG.DRAFT;
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => {
        const currency = record.currency?.toUpperCase() || "USD";
        const formattedAmount = (amount / 100).toFixed(2);
        return (
          <span style={{ fontWeight: 600, fontSize: "15px" }}>
            ${formattedAmount} {currency}
          </span>
        );
      },
    },
    {
      title: "Invoice",
      dataIndex: "invoiceUrl",
      key: "invoiceUrl",
      render: (url) => {
        if (!url) return "-";
        return (
          <Button
            type="link"
            size="small"
            onClick={() => window.open(url, "_blank")}
            style={{ padding: 0 }}
          >
            View on Stripe
          </Button>
        );
      },
    },
  ];

  // Show loading state
  if (dataLoading) {
    return (
      <Container>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" tip="Loading subscription information..." />
        </div>
      </Container>
    );
  }

  // Show message if no subscription data
  if (!subscription) {
    return (
      <Container>
        <div className="settings-header">
          <div className="settings-header-left">
            <h1 className="settings-title">Manage Subscription</h1>
            <p className="settings-subtitle">
              Manage your subscription and billing
            </p>
          </div>
        </div>
        <Card>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p>No active subscription found. Start your free trial today!</p>
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/pricing")}
              style={{ marginTop: "16px" }}
            >
              Get Started
            </Button>
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <div className="settings-header">
        <div className="settings-header-left">
          <h1 className="settings-title">Billing & Invoices</h1>
          <p className="settings-subtitle">
            Manage your subscription and billing
          </p>
        </div>
      </div>

      <Card className="subscription-card" title="Subscription">
        <div className="subscription-content">
          <div className="subscription-status">
            <Tag
              icon={statusConfig.icon}
              color={statusConfig.color}
              className="status-tag"
            >
              {statusConfig.text}
            </Tag>
          </div>

          <Table
            dataSource={tableData}
            columns={columns}
            pagination={false}
            className="subscription-details"
            bordered
            style={{ marginBottom: "24px" }}
          />

          <div className="subscription-actions">
            {[
              "TRIAL",
              "PAST_DUE",
              "CANCELED",
              "UNPAID",
              "INCOMPLETE",
              "PAUSED",
            ].includes(subscription.status) && (
              <Button
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
                loading={loading}
                onClick={handleUpgrade}
              >
                Manage Billing
              </Button>
            )}
            {["ACTIVE", "TRIALING"].includes(subscription.status) && (
              <Button
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
                loading={loading}
                onClick={handleManageSubscription}
              >
                Manage Subscription
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="subscription-card" title="Invoices">
        <Table
          dataSource={invoices}
          columns={invoiceColumns}
          pagination={false}
          locale={{
            emptyText: "No invoices available",
          }}
          bordered
          rowKey="stripeInvoiceId"
        />
      </Card>
    </Container>
  );
}
